/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { Dispatcher } from 'src/core/types/dispatcher';
import { GlobalHandlerGroup } from 'src/core/types/GlobalHandlerGroup';
import { AddToastParameters } from 'src/core/utils/decorators/addToast';
import { EntityHandlerGroup } from 'src/luis/projects/entities/handlers/entityHandlerGroup';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { HierarchicalEntity } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { ListEntity } from 'src/luis/projects/entities/types/ListEntity';
import { PatternAnyEntity } from 'src/luis/projects/entities/types/PatternAnyEntity';
import { RegexEntity } from 'src/luis/projects/entities/types/RegexEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isEntityOfType } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Callback function to handle the create new entity action.
 *
 * @param entity The entity object to be added
 * @param dispatcher The dispatcher object to access the handlers.
 * @param tracker The progress tracker to track the entity creation.
 */
export const createEntity = (
    entity: Entity,
    dispatcher: Dispatcher<GlobalHandlerGroup>,
    tracker: ProgressTrackerStore,
    toastData: AddToastParameters['toastData'],
    onComplete?: (id: LuisId) => void
): void => {
    const payload = { name: entity.name, toastData, tracker, onComplete };

    if (isEntityOfType<SimpleEntity>(entity, ModelType.Simple)) {
        dispatcher.dispatch('createSimpleEntity', {
            ...payload,
            features: entity.features,
            children: entity.children
        });

        return;
    }

    if (isEntityOfType<CompositeEntity>(entity, ModelType.Composite)) {
        dispatcher.dispatch('createCompositeEntity', { ...payload, children: entity.children.map(c => c.name) });

        return;
    }

    if (isEntityOfType<ListEntity>(entity, ModelType.List)) {
        dispatcher.dispatch('createListEntity', { ...payload, sublists: entity.sublists });

        return;
    }

    if (isEntityOfType<RegexEntity>(entity, ModelType.Regex)) {
        dispatcher.dispatch('createRegexEntity', { ...payload, pattern: entity.pattern });

        return;
    }

    if (isEntityOfType<PatternAnyEntity>(entity, ModelType.PatternAny)) {
        dispatcher.dispatch('createPatternAnyEntity', payload);
    }
};

/**
 * Callback function to handle renaming an entity.
 */
export const renameEntity = (
    entity: Entity,
    newName: string,
    dispatcher: Dispatcher<GlobalHandlerGroup>,
    tracker: ProgressTrackerStore,
    toastData: AddToastParameters['toastData']
): void => {
    const payload = { id: entity.id, name: newName, toastData, tracker };

    if (isEntityOfType<SimpleEntity>(entity, ModelType.Simple)) {
        dispatcher.dispatch('renameSimpleEntity', payload);

        return;
    }

    if (isEntityOfType<CompositeEntity>(entity, ModelType.Composite)) {
        dispatcher.dispatch('updateCompositeEntity', { ...payload, children: entity.children });

        return;
    }

    if (isEntityOfType<ListEntity>(entity, ModelType.List)) {
        dispatcher.dispatch('updateListEntity', { ...payload, sublists: entity.sublists });

        return;
    }

    if (isEntityOfType<RegexEntity>(entity, ModelType.Regex)) {
        dispatcher.dispatch('updateRegexEntity', { ...payload, pattern: entity.pattern });

        return;
    }

    if (isEntityOfType<HierarchicalEntity>(entity, ModelType.Hierarchical)) {
        dispatcher.dispatch('renameHierarchicalEntity', { ...payload, children: entity.children });

        return;
    }

    if (isEntityOfType<PatternAnyEntity>(entity, ModelType.PatternAny)) {
        dispatcher.dispatch('renamePatternAnyEntity', payload);
    }
};

/**
 * Callback function to handle deleting entities.
 */
export const deleteEntity = (
    entities: { id: LuisId; typeId: ModelType }[],
    dispatcher: Dispatcher<GlobalHandlerGroup>,
    tracker: ProgressTrackerStore,
    toastDataSingle: AddToastParameters['toastData'],
    toastDataMultiple: AddToastParameters['toastData']
): void => {
    // Ensures that composite entities are deleted before other children entities.
    const entitiesToDelete = entities.slice().sort(entity => (entity.typeId === ModelType.Composite ? -1 : 1));
    let handlerName: keyof EntityHandlerGroup;

    entitiesToDelete.forEach(entity => {
        if (tracker && tracker.isRunning && tracker.context === entity.id) {
            return;
        }

        const payload = { id: entity.id, tracker, toastData: entitiesToDelete.length === 1 ? toastDataSingle : toastDataMultiple };

        switch (entity.typeId) {
            case ModelType.Simple:
                handlerName = 'deleteSimpleEntity';
                break;

            case ModelType.Composite:
                handlerName = 'deleteCompositeEntity';
                break;

            case ModelType.List:
                handlerName = 'deleteListEntity';
                break;

            case ModelType.PatternAny:
                handlerName = 'deletePatternAnyEntity';
                break;

            case ModelType.Regex:
                handlerName = 'deleteRegexEntity';
                break;

            case ModelType.Prebuilt:
                handlerName = 'deletePrebuiltEntities';
                break;

            case ModelType.Hierarchical:
                handlerName = 'deleteHierarchicalEntity';
                break;
            default:
                throw Error('Not a valid entity type');
        }

        dispatcher.dispatch(handlerName, payload);
    });
};
